/**
 * TotalKlickADS · Calendar / Editorial Plan view
 * Route: /totalklickads/brand/:brandId/calendar
 *
 * Le date NON sono rigide: il piano editoriale contiene N slot che
 * l'utente può ridistribuire liberamente con drag&drop tra giorni
 * (range 7-60 giorni dalla data di partenza del piano).
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ArrowLeft, Plus, Sparkles, Trash2, Calendar as CalendarIcon, List as ListIcon,
    ChevronLeft, ChevronRight, X, Wand2, ExternalLink, Image as ImageIcon, AlertCircle,
    Camera, Layers, Loader2, Send,
} from "lucide-react";
import {
    DndContext, useDraggable, useDroppable, DragOverlay,
    PointerSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import { api, formatApiError } from "@/lib/api";
import ModuleAccent from "@/components/ModuleAccent";

const PRIMARY = "#7C3AED";
const ACCENT = "#FF1A5C";

const PLATFORMS = [
    { id: "instagram", label: "IG", color: "#E1306C" },
    { id: "facebook", label: "FB", color: "#1877F2" },
    { id: "linkedin", label: "Li", color: "#0A66C2" },
    { id: "x", label: "X", color: "#FFF" },
    { id: "tiktok", label: "TT", color: "#69C9D0" },
    { id: "threads", label: "Th", color: "#A0A0A0" },
];

const STATUS_META = {
    draft:     { color: "#9CA3AF", bg: "rgba(156,163,175,0.18)", label: "Bozza" },
    approved:  { color: "#60A5FA", bg: "rgba(96,165,250,0.18)",  label: "Approvato" },
    scheduled: { color: "#FBBF24", bg: "rgba(251,191,36,0.18)",  label: "Programmato" },
    published: { color: "#34D399", bg: "rgba(52,211,153,0.18)",  label: "Pubblicato" },
    failed:    { color: "#F87171", bg: "rgba(248,113,113,0.18)", label: "Errore" },
};

const FORMATS = [
    { id: "feed_square",   label: "Post 1:1",   ratio: "1:1",  hint: "IG/FB feed" },
    { id: "feed_portrait", label: "Post 4:5",   ratio: "4:5",  hint: "IG/LinkedIn portrait" },
    { id: "reel",          label: "Reel 9:16",  ratio: "9:16", hint: "IG/TikTok/Reels" },
    { id: "story",         label: "Story 9:16", ratio: "9:16", hint: "IG/FB Stories" },
    { id: "carousel",      label: "Carosello",  ratio: "1:1",  hint: "IG/LinkedIn slides" },
    { id: "video",         label: "Video 16:9", ratio: "16:9", hint: "LinkedIn/X/YouTube" },
];

const isoDate = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

export default function TotalKlickADSCalendar() {
    const { brandId } = useParams();
    const [brand, setBrand] = useState(null);
    const [plans, setPlans] = useState([]);
    const [activePlanId, setActivePlanId] = useState(null);
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showWizard, setShowWizard] = useState(false);
    const [view, setView] = useState("month");   // "month" | "list"
    const [refMonth, setRefMonth] = useState(new Date()); // first day visible
    const [openSlotId, setOpenSlotId] = useState(null);
    const [draggingSlot, setDraggingSlot] = useState(null);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    const loadInitial = useCallback(async () => {
        try {
            const [brandRes, plansRes] = await Promise.all([
                api.get(`/ads/brands/${brandId}`),
                api.get(`/ads/brands/${brandId}/editorial-plans`),
            ]);
            setBrand(brandRes.data.brand);
            const ps = plansRes.data || [];
            setPlans(ps);
            // Default: piano più recente attivo
            const first = ps.find(p => p.status === "active") || ps[0];
            if (first) setActivePlanId(first.id);
        } catch (e) { setError(formatApiError(e)); }
        setLoading(false);
    }, [brandId]);

    useEffect(() => { loadInitial(); }, [loadInitial]);

    const loadSlots = useCallback(async () => {
        if (!activePlanId) { setSlots([]); return; }
        try {
            const { data } = await api.get(`/ads/brands/${brandId}/scheduled-posts`, {
                params: { plan_id: activePlanId },
            });
            setSlots(data || []);
        } catch (e) { setError(formatApiError(e)); }
    }, [brandId, activePlanId]);

    useEffect(() => { loadSlots(); }, [loadSlots]);

    const activePlan = plans.find(p => p.id === activePlanId) || null;

    // ----- Drag & drop handlers -----
    const onDragStart = (e) => {
        const slot = slots.find(s => s.id === e.active.id);
        setDraggingSlot(slot || null);
    };

    const onDragEnd = async (e) => {
        const { active, over } = e;
        setDraggingSlot(null);
        if (!over) return;
        const slot = slots.find(s => s.id === active.id);
        if (!slot) return;
        const newDate = String(over.id);
        if (slot.scheduled_date === newDate) return;
        // optimistic update
        setSlots(prev => prev.map(s => s.id === slot.id ? { ...s, scheduled_date: newDate } : s));
        try {
            await api.patch(`/ads/brands/${brandId}/scheduled-posts/${slot.id}`, {
                scheduled_date: newDate,
            });
        } catch (e) {
            setError(formatApiError(e));
            loadSlots();  // revert
        }
    };

    // ----- Slot CRUD -----
    const onSlotDelete = async (slotId) => {
        if (!window.confirm("Eliminare questo slot?")) return;
        try {
            await api.delete(`/ads/brands/${brandId}/scheduled-posts/${slotId}`);
            setSlots(prev => prev.filter(s => s.id !== slotId));
            setOpenSlotId(null);
        } catch (e) { setError(formatApiError(e)); }
    };

    const onSlotPatch = async (slotId, patch) => {
        try {
            await api.patch(`/ads/brands/${brandId}/scheduled-posts/${slotId}`, patch);
            setSlots(prev => prev.map(s => s.id === slotId ? { ...s, ...patch } : s));
        } catch (e) { setError(formatApiError(e)); }
    };

    const onAddEmptySlot = async (dateIso) => {
        if (!activePlanId) return;
        try {
            const { data } = await api.post(`/ads/brands/${brandId}/scheduled-posts`, {
                plan_id: activePlanId,
                scheduled_date: dateIso,
                scheduled_time: "10:00",
                title: "Nuovo post",
                copy_by_platform: {},
                status: "draft",
            });
            await loadSlots();
            setOpenSlotId(data.id);
        } catch (e) { setError(formatApiError(e)); }
    };

    const onPlanDelete = async () => {
        if (!activePlanId) return;
        if (!window.confirm("Eliminare il piano e TUTTI i suoi slot?")) return;
        try {
            await api.delete(`/ads/brands/${brandId}/editorial-plans/${activePlanId}`);
            setActivePlanId(null);
            setPlans(prev => prev.filter(p => p.id !== activePlanId));
            setSlots([]);
        } catch (e) { setError(formatApiError(e)); }
    };

    // ----- Calendar grid -----
    const monthGrid = useMemo(() => buildMonthGrid(refMonth), [refMonth]);
    const slotsByDate = useMemo(() => {
        const m = {};
        for (const s of slots) {
            (m[s.scheduled_date] = m[s.scheduled_date] || []).push(s);
        }
        return m;
    }, [slots]);

    const openedSlot = slots.find(s => s.id === openSlotId);

    return (
        <ModuleAccent module="ads" as="div" style={{ fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif', background: "#0A1024", color: "#fff", minHeight: "100vh" }} data-testid="ads-calendar-page">
            {/* Topbar */}
            <header className="px-6 md:px-10 py-3.5 flex items-center justify-between sticky top-0 z-40 backdrop-blur-md" style={{ background: "rgba(10,16,36,0.92)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                <div className="flex items-center gap-6">
                    <Link to="/totalklickads/dashboard" className="flex items-center gap-2">
                        <img src="/brand/totalklickads/logo.png" alt="TotalKlickADS" className="h-7" />
                    </Link>
                    <nav className="hidden md:flex items-center gap-1 text-[12.5px] font-bold">
                        <Link to="/totalklickads/dashboard" className="px-3 py-1.5 opacity-60 hover:opacity-100">Brand</Link>
                        <span className="px-3 py-1.5 rounded-full" style={{ background: "rgba(124,58,237,0.18)", color: "#C4B5FD" }}>Calendario</span>
                        <Link to={`/totalklickads/brand/${brandId}/settings`} className="px-3 py-1.5 opacity-60 hover:opacity-100" data-testid="ads-nav-settings">Integrazioni</Link>
                        <span className="px-3 py-1.5 opacity-30 cursor-not-allowed">Analytics</span>
                    </nav>
                </div>
                <Link to={`/totalklickads/brand/${brandId}`} className="text-[12px] opacity-60 hover:opacity-100 inline-flex items-center gap-1"><ArrowLeft size={12} /> Brand</Link>
            </header>

            {/* Hero / plan selector */}
            <section className="px-6 md:px-10 py-7 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-[10.5px] mono uppercase tracking-[0.25em] opacity-50 mb-1">{brand?.name || "..."}</div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className="text-[24px] md:text-[30px] font-extrabold tracking-tighter">Piano editoriale</h1>
                            {plans.length > 0 && (
                                <select value={activePlanId || ""} onChange={(e) => setActivePlanId(e.target.value)}
                                    className="px-3 py-2 rounded-full text-[13px] font-bold bg-transparent outline-none cursor-pointer"
                                    style={{ background: "rgba(124,58,237,0.12)", border: "1px solid rgba(124,58,237,0.35)", color: "#C4B5FD" }}
                                    data-testid="ads-plan-selector">
                                    {plans.map(p => (
                                        <option key={p.id} value={p.id} style={{ background: "#1A1F36" }}>
                                            {p.name} · {p.total_slots} slot
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                        {activePlan && (
                            <div className="text-[12px] opacity-60 mt-1">
                                {activePlan.start_date} → {activePlan.end_date}
                                {" · "}{(activePlan.platforms || []).join(", ")}
                                {activePlan.source === "ai" && <span className="ml-2 px-1.5 py-0.5 rounded text-[9.5px] mono uppercase tracking-wider" style={{ background: "rgba(124,58,237,0.18)", color: "#C4B5FD" }}>AI</span>}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {activePlanId && (
                            <button onClick={onPlanDelete} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-bold opacity-60 hover:opacity-100" style={{ border: "1px solid rgba(255,255,255,0.12)" }} data-testid="ads-plan-delete">
                                <Trash2 size={12} /> Elimina piano
                            </button>
                        )}
                        <button onClick={() => setShowWizard(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-extrabold text-[13px] text-white" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, boxShadow: `0 10px 28px ${PRIMARY}45` }} data-testid="ads-generate-plan">
                            <Sparkles size={14} /> Genera piano AI
                        </button>
                    </div>
                </div>
            </section>

            {error && (
                <div className="max-w-7xl mx-auto px-6 md:px-10 mt-4">
                    <div className="p-3 rounded-lg text-[13px] inline-flex items-center gap-2" style={{ background: "rgba(239,68,68,0.12)", color: "#FCA5A5" }}>
                        <AlertCircle size={14} /> {error}
                    </div>
                </div>
            )}

            {/* Body */}
            <section className="px-6 md:px-10 py-6">
                <div className="max-w-7xl mx-auto">
                    {loading ? (
                        <div className="opacity-60 py-16 text-center">Caricamento...</div>
                    ) : !activePlanId ? (
                        <EmptyState onGenerate={() => setShowWizard(true)} />
                    ) : (
                        <>
                            {/* View toggle + month navigator */}
                            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                                <div className="inline-flex rounded-full p-1" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                                    <button onClick={() => setView("month")} className={`px-3 py-1.5 rounded-full text-[12px] font-bold inline-flex items-center gap-1.5 ${view === "month" ? "text-white" : "opacity-50"}`} style={view === "month" ? { background: PRIMARY } : {}} data-testid="ads-view-month">
                                        <CalendarIcon size={12} /> Mese
                                    </button>
                                    <button onClick={() => setView("list")} className={`px-3 py-1.5 rounded-full text-[12px] font-bold inline-flex items-center gap-1.5 ${view === "list" ? "text-white" : "opacity-50"}`} style={view === "list" ? { background: PRIMARY } : {}} data-testid="ads-view-list">
                                        <ListIcon size={12} /> Lista
                                    </button>
                                </div>
                                {view === "month" && (
                                    <div className="inline-flex items-center gap-2">
                                        <button onClick={() => setRefMonth(addMonths(refMonth, -1))} className="w-8 h-8 rounded-full grid place-items-center" style={{ background: "rgba(255,255,255,0.06)" }}><ChevronLeft size={14} /></button>
                                        <div className="text-[14px] font-extrabold w-44 text-center">{monthLabel(refMonth)}</div>
                                        <button onClick={() => setRefMonth(addMonths(refMonth, 1))} className="w-8 h-8 rounded-full grid place-items-center" style={{ background: "rgba(255,255,255,0.06)" }}><ChevronRight size={14} /></button>
                                    </div>
                                )}
                                <div className="text-[12px] opacity-60">{slots.length} slot</div>
                            </div>

                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd}>
                                {view === "month" ? (
                                    <MonthGrid grid={monthGrid} refMonth={refMonth} slotsByDate={slotsByDate} onOpenSlot={setOpenSlotId} onAddEmpty={onAddEmptySlot} />
                                ) : (
                                    <ListView slots={[...slots].sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))} onOpenSlot={setOpenSlotId} />
                                )}
                                <DragOverlay>
                                    {draggingSlot && <SlotCardPreview slot={draggingSlot} />}
                                </DragOverlay>
                            </DndContext>
                        </>
                    )}
                </div>
            </section>

            {/* Wizard modal */}
            {showWizard && (
                <GenerateWizard
                    brandId={brandId}
                    onClose={() => setShowWizard(false)}
                    onCreated={async (planId) => {
                        setShowWizard(false);
                        const { data } = await api.get(`/ads/brands/${brandId}/editorial-plans`);
                        setPlans(data || []);
                        setActivePlanId(planId);
                    }}
                />
            )}

            {/* Slot detail modal */}
            {openedSlot && (
                <SlotDetailModal
                    brandId={brandId}
                    slot={openedSlot}
                    platforms={activePlan?.platforms || []}
                    onClose={() => setOpenSlotId(null)}
                    onPatch={(patch) => onSlotPatch(openedSlot.id, patch)}
                    onDelete={() => onSlotDelete(openedSlot.id)}
                    onReload={loadSlots}
                />
            )}
        </ModuleAccent>
    );
}

/* ========================== EMPTY STATE ========================== */
function EmptyState({ onGenerate }) {
    return (
        <div className="rounded-2xl p-12 text-center" style={{ background: "rgba(124,58,237,0.06)", border: "1px dashed rgba(124,58,237,0.3)" }} data-testid="ads-calendar-empty">
            <Sparkles size={36} className="mx-auto mb-3" style={{ color: PRIMARY }} />
            <h3 className="text-[18px] font-extrabold">Nessun piano editoriale</h3>
            <p className="text-[13px] opacity-70 mt-1 max-w-md mx-auto">Genera il tuo primo piano con l&apos;AI: scegli durata, numero di post e tema. Poi sposta liberamente gli slot tra i giorni con il drag&amp;drop.</p>
            <button onClick={onGenerate} className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-extrabold text-[13px] text-white" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})` }} data-testid="ads-empty-generate">
                <Wand2 size={14} /> Genera piano AI
            </button>
        </div>
    );
}

/* ========================== MONTH GRID ========================== */
function MonthGrid({ grid, refMonth, slotsByDate, onOpenSlot, onAddEmpty }) {
    const dows = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
    return (
        <div className="rounded-2xl overflow-hidden" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="grid grid-cols-7 text-[10px] uppercase tracking-[0.16em] font-bold opacity-50" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                {dows.map(d => <div key={d} className="px-2 py-2 text-center">{d}</div>)}
            </div>
            <div className="grid grid-cols-7" data-testid="ads-month-grid">
                {grid.map((d, i) => (
                    <DayCell key={i} date={d} isCurrentMonth={d.getMonth() === refMonth.getMonth()}
                        slots={slotsByDate[isoDate(d)] || []}
                        onOpenSlot={onOpenSlot} onAddEmpty={() => onAddEmpty(isoDate(d))} />
                ))}
            </div>
        </div>
    );
}

function DayCell({ date, isCurrentMonth, slots, onOpenSlot, onAddEmpty }) {
    const id = isoDate(date);
    const { setNodeRef, isOver } = useDroppable({ id });
    const isToday = isoDate(new Date()) === id;
    return (
        <div ref={setNodeRef}
            className="min-h-[120px] p-1.5 relative group transition"
            style={{
                borderRight: "1px solid rgba(255,255,255,0.04)",
                borderBottom: "1px solid rgba(255,255,255,0.04)",
                background: isOver ? "rgba(124,58,237,0.18)" : "transparent",
                opacity: isCurrentMonth ? 1 : 0.35,
            }}
            data-testid={`ads-day-${id}`}>
            <div className="flex items-center justify-between mb-1">
                <div className={`text-[11px] font-extrabold ${isToday ? "" : ""}`} style={{
                    color: isToday ? "#FFF" : "rgba(255,255,255,0.6)",
                    background: isToday ? PRIMARY : "transparent",
                    borderRadius: 999,
                    width: 20, height: 20,
                    display: "grid", placeItems: "center",
                }}>{date.getDate()}</div>
                <button onClick={onAddEmpty} className="opacity-0 group-hover:opacity-100 w-5 h-5 rounded grid place-items-center" style={{ background: "rgba(255,255,255,0.08)" }} title="Aggiungi slot vuoto"><Plus size={10} /></button>
            </div>
            <div className="space-y-1">
                {slots.map(s => <DraggableSlotChip key={s.id} slot={s} onClick={() => onOpenSlot(s.id)} />)}
            </div>
        </div>
    );
}

/* ========================== DRAGGABLE SLOT CHIP ========================== */
function DraggableSlotChip({ slot, onClick }) {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: slot.id });
    const st = STATUS_META[slot.status] || STATUS_META.draft;
    return (
        <div ref={setNodeRef}
            style={{
                transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
                opacity: isDragging ? 0.3 : 1,
                background: st.bg,
                borderLeft: `3px solid ${st.color}`,
            }}
            {...attributes} {...listeners}
            onClick={(e) => { if (!isDragging) onClick?.(); e.stopPropagation(); }}
            className="rounded-md px-1.5 py-1 cursor-grab active:cursor-grabbing text-[10.5px] leading-tight"
            data-testid={`ads-slot-chip-${slot.id}`}>
            <div className="flex items-center gap-1 mb-0.5">
                <span className="opacity-70 mono">{(slot.scheduled_time || "10:00").slice(0, 5)}</span>
                <PlatformBadges platforms={Object.keys(slot.copy_by_platform || {})} compact />
            </div>
            <div className="font-bold truncate" style={{ color: "rgba(255,255,255,0.9)" }}>{slot.title || slot.theme || "—"}</div>
        </div>
    );
}

function SlotCardPreview({ slot }) {
    return (
        <div className="rounded-md px-2 py-1.5 text-[11px] shadow-2xl" style={{
            background: "#1A1F36", border: "1px solid rgba(124,58,237,0.5)",
            boxShadow: `0 12px 36px ${PRIMARY}55`,
        }}>
            <div className="font-bold truncate max-w-[180px]">{slot.title || slot.theme || "—"}</div>
            <div className="opacity-60 mono">{slot.scheduled_time}</div>
        </div>
    );
}

/* ========================== LIST VIEW ========================== */
function ListView({ slots, onOpenSlot }) {
    if (slots.length === 0) {
        return <div className="opacity-60 py-10 text-center text-[13px]">Nessuno slot in questo piano.</div>;
    }
    // group by date
    const groups = {};
    for (const s of slots) (groups[s.scheduled_date] = groups[s.scheduled_date] || []).push(s);
    const dates = Object.keys(groups).sort();
    return (
        <div className="space-y-5" data-testid="ads-list-view">
            {dates.map(d => (
                <div key={d}>
                    <div className="text-[11px] mono uppercase tracking-[0.16em] opacity-60 mb-2">{formatLongDate(d)}</div>
                    <div className="grid md:grid-cols-2 gap-2">
                        {groups[d].map(s => {
                            const st = STATUS_META[s.status] || STATUS_META.draft;
                            return (
                                <button key={s.id} onClick={() => onOpenSlot(s.id)} className="rounded-xl p-3 text-left transition hover:scale-[1.01]"
                                    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderLeft: `3px solid ${st.color}` }}
                                    data-testid={`ads-list-slot-${s.id}`}>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-[10.5px] mono opacity-60">{s.scheduled_time}</span>
                                        <span className="text-[9.5px] mono uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                                    </div>
                                    <div className="font-extrabold text-[13.5px] leading-tight">{s.title || s.theme || "—"}</div>
                                    {s.brief && <div className="text-[11.5px] opacity-60 mt-1 line-clamp-2 leading-relaxed">{s.brief}</div>}
                                    <div className="mt-2"><PlatformBadges platforms={Object.keys(s.copy_by_platform || {})} /></div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ========================== SLOT DETAIL MODAL ========================== */
function SlotDetailModal({ brandId, slot, platforms, onClose, onPatch, onDelete, onReload }) {
    const [local, setLocal] = useState(slot);
    const [generating, setGenerating] = useState(false);
    const [genError, setGenError] = useState("");
    const pendingPatchRef = React.useRef({});
    const timerRef = React.useRef(null);
    useEffect(() => setLocal(slot), [slot.id]);  // eslint-disable-line react-hooks/exhaustive-deps

    // Flush pending patches on unmount/close
    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            if (Object.keys(pendingPatchRef.current).length > 0) {
                onPatch(pendingPatchRef.current);
                pendingPatchRef.current = {};
            }
        };
    }, [onPatch]);

    const platformList = Array.from(new Set([
        ...(platforms || []),
        ...Object.keys(slot.copy_by_platform || {}),
    ]));
    const [activeTab, setActiveTab] = useState(platformList[0] || "instagram");

    /* Debounced PATCH che ACCUMULA i campi modificati: se l'utente cambia titolo
       e poi status entro 600ms, entrambi i campi finiscono nello stesso PATCH. */
    const change = (patch) => {
        setLocal(prev => ({ ...prev, ...patch }));
        pendingPatchRef.current = { ...pendingPatchRef.current, ...patch };
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            const merged = pendingPatchRef.current;
            pendingPatchRef.current = {};
            if (Object.keys(merged).length > 0) onPatch(merged);
        }, 600);
    };

    const changeCopy = (platform, fieldPatch) => {
        const cbp = { ...(local.copy_by_platform || {}) };
        cbp[platform] = { ...(cbp[platform] || {}), ...fieldPatch };
        change({ copy_by_platform: cbp });
    };

    const tabCopy = (local.copy_by_platform || {})[activeTab] || {};

    const canvaUrl = `https://www.canva.com/create/social-graphics/`;

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(0,0,0,0.7)" }} onClick={onClose}>
            <div className="rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" style={{ background: "#1A1F36", border: "1px solid rgba(255,255,255,0.1)" }} onClick={(e) => e.stopPropagation()} data-testid="ads-slot-modal">
                {/* Header */}
                <div className="flex items-start justify-between p-5 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                    <div className="flex-1 min-w-0">
                        <input value={local.title || ""} onChange={(e) => change({ title: e.target.value })}
                            placeholder="Titolo del post"
                            className="w-full bg-transparent outline-none text-[18px] font-extrabold"
                            data-testid="ads-slot-title" />
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <input type="date" value={local.scheduled_date} onChange={(e) => change({ scheduled_date: e.target.value })}
                                className="bg-transparent text-[12px] mono px-2 py-1 rounded outline-none"
                                style={{ background: "rgba(255,255,255,0.06)" }} data-testid="ads-slot-date" />
                            <input type="time" value={(local.scheduled_time || "10:00").slice(0, 5)} onChange={(e) => change({ scheduled_time: e.target.value })}
                                className="bg-transparent text-[12px] mono px-2 py-1 rounded outline-none"
                                style={{ background: "rgba(255,255,255,0.06)" }} data-testid="ads-slot-time" />
                            <select value={local.status || "draft"} onChange={(e) => change({ status: e.target.value })}
                                className="bg-transparent text-[12px] px-2 py-1 rounded outline-none font-bold"
                                style={{ background: STATUS_META[local.status]?.bg || "rgba(255,255,255,0.06)", color: STATUS_META[local.status]?.color || "#FFF" }}
                                data-testid="ads-slot-status">
                                {Object.entries(STATUS_META).map(([k, v]) => (
                                    <option key={k} value={k} style={{ background: "#1A1F36", color: "#FFF" }}>{v.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 rounded-full grid place-items-center opacity-60 hover:opacity-100" style={{ background: "rgba(255,255,255,0.06)" }} data-testid="ads-slot-close"><X size={14} /></button>
                </div>

                {/* Brief + visual + format + media */}
                <div className="p-5 space-y-4 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                    {/* Format selector */}
                    <div>
                        <label className="text-[10.5px] mono uppercase tracking-wider opacity-60 flex items-center gap-1.5">
                            <Layers size={11} /> Formato output
                        </label>
                        <div className="grid grid-cols-3 gap-1.5 mt-2">
                            {FORMATS.map(f => (
                                <button key={f.id} type="button"
                                    onClick={() => change({ format: f.id, aspect_ratio: f.ratio })}
                                    className="px-2 py-2 rounded-lg text-[11px] font-bold text-left transition"
                                    style={{
                                        background: local.format === f.id ? "rgba(124,58,237,0.22)" : "rgba(255,255,255,0.04)",
                                        color: local.format === f.id ? "#C4B5FD" : "rgba(255,255,255,0.7)",
                                        border: `1px solid ${local.format === f.id ? "rgba(124,58,237,0.45)" : "rgba(255,255,255,0.08)"}`,
                                    }}
                                    data-testid={`ads-slot-format-${f.id}`}>
                                    <div className="font-extrabold">{f.label}</div>
                                    <div className="text-[9.5px] opacity-60 mt-0.5">{f.hint}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Publish on multi-select */}
                    <div>
                        <label className="text-[10.5px] mono uppercase tracking-wider opacity-60 flex items-center gap-1.5">
                            <Send size={11} /> Pubblica su (selezionare le piattaforme)
                        </label>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                            {platformList.map(p => {
                                const active = (local.publish_platforms || []).includes(p);
                                const meta = PLATFORMS.find(x => x.id === p) || { label: p, color: "#FFF" };
                                return (
                                    <button key={p} type="button"
                                        onClick={() => {
                                            const cur = local.publish_platforms || [];
                                            const next = active ? cur.filter(x => x !== p) : [...cur, p];
                                            change({ publish_platforms: next });
                                        }}
                                        className="px-3 py-1.5 rounded-full text-[11.5px] font-bold transition inline-flex items-center gap-1.5"
                                        style={{
                                            background: active ? `${meta.color}28` : "rgba(255,255,255,0.04)",
                                            color: active ? meta.color : "rgba(255,255,255,0.55)",
                                            border: `1px solid ${active ? `${meta.color}60` : "rgba(255,255,255,0.08)"}`,
                                        }}
                                        data-testid={`ads-slot-publish-${p}`}>
                                        <span className="mono text-[10px]">{meta.label}</span> {p}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <label className="text-[10.5px] mono uppercase tracking-wider opacity-60">Brief / idea</label>
                        <textarea value={local.brief || ""} onChange={(e) => change({ brief: e.target.value })}
                            rows={2}
                            className="w-full bg-transparent text-[13px] mt-1 px-3 py-2 rounded-lg outline-none resize-none"
                            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                            placeholder="Cosa racconta questo post?" />
                    </div>
                    <div>
                        <label className="text-[10.5px] mono uppercase tracking-wider opacity-60 flex items-center gap-1.5">
                            <ImageIcon size={11} /> Visual brief
                        </label>
                        <textarea value={local.visual_brief || ""} onChange={(e) => change({ visual_brief: e.target.value })}
                            rows={2}
                            className="w-full bg-transparent text-[13px] mt-1 px-3 py-2 rounded-lg outline-none resize-none"
                            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                            placeholder="Descrizione scena, composizione, atmosfera..." />
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <button type="button" disabled={generating || !(local.visual_brief || "").trim()}
                                onClick={async () => {
                                    setGenError(""); setGenerating(true);
                                    try {
                                        const count = local.format === "carousel" ? 3 : 1;
                                        await api.post(`/ads/brands/${brandId}/scheduled-posts/${local.id}/generate-image`, { count });
                                        // Reload slot data via parent
                                        if (onReload) await onReload();
                                        // Refresh local from parent re-fetch (slot prop updates next render)
                                    } catch (e) { setGenError(formatApiError(e)); }
                                    setGenerating(false);
                                }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-bold disabled:opacity-50"
                                style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, color: "#FFF" }}
                                data-testid="ads-slot-generate-image">
                                {generating
                                    ? (<><Loader2 size={11} className="animate-spin" /> Generazione (15-30s)...</>)
                                    : (<><Camera size={11} /> {local.format === "carousel" ? "Genera carosello (3 img)" : "Genera immagine AI"}</>)
                                }
                            </button>
                            <CinematicVideoButton brandId={brandId} slot={local} onDone={onReload} />
                            <a href={canvaUrl} target="_blank" rel="noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold"
                                style={{ background: "rgba(0,194,255,0.12)", color: "#7DD3FC", border: "1px solid rgba(0,194,255,0.25)" }}
                                data-testid="ads-slot-canva">
                                Apri Canva <ExternalLink size={10} />
                            </a>
                        </div>
                        {genError && <div className="mt-2 text-[11.5px] px-2 py-1.5 rounded" style={{ background: "rgba(239,68,68,0.10)", color: "#FCA5A5" }}>{genError}</div>}
                    </div>

                    {/* Media outputs preview */}
                    {(local.media_outputs || []).length > 0 && (
                        <div>
                            <label className="text-[10.5px] mono uppercase tracking-wider opacity-60">Output media</label>
                            <div className="grid grid-cols-3 gap-2 mt-2">
                                {(local.media_outputs || []).map((m, idx) => (
                                    <MediaThumb key={idx} brandId={brandId} media={m} slotId={local.id}
                                        onRemoved={async () => { if (onReload) await onReload(); }} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Copy per platform */}
                <div className="p-5">
                    <div className="text-[10.5px] mono uppercase tracking-wider opacity-60 mb-2">Copy per piattaforma</div>
                    <div className="inline-flex flex-wrap gap-1 mb-3">
                        {platformList.map(p => {
                            const meta = PLATFORMS.find(x => x.id === p) || { label: p, color: "#FFF" };
                            return (
                                <button key={p} onClick={() => setActiveTab(p)}
                                    className="px-2.5 py-1 rounded-full text-[11px] font-bold transition"
                                    style={{
                                        background: activeTab === p ? "rgba(124,58,237,0.25)" : "rgba(255,255,255,0.04)",
                                        color: activeTab === p ? "#C4B5FD" : "rgba(255,255,255,0.6)",
                                        border: `1px solid ${activeTab === p ? "rgba(124,58,237,0.4)" : "rgba(255,255,255,0.08)"}`,
                                    }}
                                    data-testid={`ads-slot-platform-${p}`}>
                                    {meta.label} · {p}
                                </button>
                            );
                        })}
                    </div>
                    <textarea value={tabCopy.copy || ""} onChange={(e) => changeCopy(activeTab, { copy: e.target.value })}
                        rows={6}
                        className="w-full bg-transparent text-[13px] px-3 py-2 rounded-lg outline-none resize-none"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                        placeholder={`Copy per ${activeTab}...`}
                        data-testid="ads-slot-copy" />
                    <div className="grid grid-cols-2 gap-2 mt-2">
                        <input value={(tabCopy.hashtags || []).join(" ")}
                            onChange={(e) => changeCopy(activeTab, { hashtags: e.target.value.split(/\s+/).filter(Boolean) })}
                            placeholder="#hashtag #separati #spazio"
                            className="bg-transparent text-[12px] px-3 py-2 rounded-lg outline-none"
                            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} />
                        <input value={tabCopy.cta || ""}
                            onChange={(e) => changeCopy(activeTab, { cta: e.target.value })}
                            placeholder="Call to action"
                            className="bg-transparent text-[12px] px-3 py-2 rounded-lg outline-none"
                            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} />
                    </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-4 border-t flex justify-between items-center" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                    <button onClick={onDelete} className="text-[12px] opacity-60 hover:opacity-100 inline-flex items-center gap-1.5" data-testid="ads-slot-delete">
                        <Trash2 size={12} /> Elimina slot
                    </button>
                    <button onClick={onClose} className="px-4 py-2 rounded-full text-[12px] font-bold text-white" style={{ background: PRIMARY }} data-testid="ads-slot-save">
                        Fatto
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ========================== CINEMATIC VIDEO BUTTON ========================== */
function CinematicVideoButton({ brandId, slot, onDone }) {
    const [open, setOpen] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [form, setForm] = useState({
        template_id: "story_kinetic",
        headline: slot.title || "",
        subhead: "",
        cta: "Scopri di più",
        duration_sec: null,
        bg_asset_id: null,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (open && templates.length === 0) {
            api.get("/ads/cinematic-templates").then(r => setTemplates(r.data || []));
        }
    }, [open, templates.length]);

    // Auto-pick first image of slot as bg if available
    useEffect(() => {
        const firstImg = (slot.media_outputs || []).find(m => m.kind === "image");
        if (firstImg && !form.bg_asset_id) setForm(p => ({ ...p, bg_asset_id: firstImg.asset_id }));
    }, [slot.media_outputs]);  // eslint-disable-line

    const submit = async () => {
        setBusy(true); setErr("");
        try {
            await api.post(`/ads/brands/${brandId}/scheduled-posts/${slot.id}/generate-video`, form);
            if (onDone) await onDone();
            setOpen(false);
        } catch (e) { setErr(formatApiError(e)); }
        setBusy(false);
    };

    return (
        <>
            <button type="button" onClick={() => setOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-bold"
                style={{ background: "rgba(255,26,92,0.12)", color: "#FCA5A5", border: "1px solid rgba(255,26,92,0.30)" }}
                data-testid="ads-slot-cinematic-video">
                <Camera size={11} /> Video cinematic
            </button>
            {open && (
                <div className="fixed inset-0 z-[60] grid place-items-center p-4" style={{ background: "rgba(0,0,0,0.75)" }} onClick={() => !busy && setOpen(false)}>
                    <div className="rounded-2xl max-w-md w-full p-5 space-y-3" style={{ background: "#0F1429", border: "1px solid rgba(255,255,255,0.1)" }} onClick={e => e.stopPropagation()} data-testid="ads-cinematic-modal">
                        <div className="flex items-center justify-between">
                            <h3 className="text-[16px] font-extrabold inline-flex items-center gap-2"><Camera size={14} style={{ color: ACCENT }} /> Video cinematic 9:16</h3>
                            <button onClick={() => !busy && setOpen(false)} className="w-7 h-7 rounded-full grid place-items-center opacity-60" style={{ background: "rgba(255,255,255,0.06)" }}><X size={12} /></button>
                        </div>

                        <div>
                            <label className="text-[10.5px] mono uppercase tracking-wider opacity-60">Template</label>
                            <div className="grid grid-cols-1 gap-1.5 mt-2">
                                {templates.map(t => (
                                    <button key={t.id} type="button"
                                        onClick={() => setForm({ ...form, template_id: t.id })}
                                        className="px-3 py-2 rounded-lg text-left transition"
                                        style={{
                                            background: form.template_id === t.id ? "rgba(255,26,92,0.15)" : "rgba(255,255,255,0.04)",
                                            border: `1px solid ${form.template_id === t.id ? "rgba(255,26,92,0.4)" : "rgba(255,255,255,0.08)"}`,
                                        }}
                                        data-testid={`ads-cinematic-tpl-${t.id}`}>
                                        <div className="font-extrabold text-[12.5px]">{t.label}</div>
                                        <div className="text-[10.5px] opacity-60 mt-0.5">{t.description} · {t.duration_sec}s default</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="text-[10.5px] mono uppercase tracking-wider opacity-60">Headline</label>
                            <input value={form.headline} onChange={e => setForm({ ...form, headline: e.target.value })}
                                className="w-full mt-1 bg-transparent text-[13px] px-3 py-2 rounded-lg outline-none"
                                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                                placeholder="Frase principale" data-testid="ads-cinematic-headline" />
                        </div>
                        <div>
                            <label className="text-[10.5px] mono uppercase tracking-wider opacity-60">Sottotitolo</label>
                            <input value={form.subhead} onChange={e => setForm({ ...form, subhead: e.target.value })}
                                className="w-full mt-1 bg-transparent text-[13px] px-3 py-2 rounded-lg outline-none"
                                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                                placeholder="Riga secondaria (opzionale)" data-testid="ads-cinematic-subhead" />
                        </div>
                        <div>
                            <label className="text-[10.5px] mono uppercase tracking-wider opacity-60">Call-to-action</label>
                            <input value={form.cta} onChange={e => setForm({ ...form, cta: e.target.value })}
                                className="w-full mt-1 bg-transparent text-[13px] px-3 py-2 rounded-lg outline-none"
                                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                                placeholder="Scopri di più" data-testid="ads-cinematic-cta" />
                        </div>

                        {(slot.media_outputs || []).find(m => m.kind === "image") && (
                            <div className="text-[11px] opacity-70 px-2.5 py-2 rounded-lg" style={{ background: "rgba(124,58,237,0.08)" }}>
                                📷 Useremo la prima immagine generata come sfondo del video
                            </div>
                        )}

                        {err && <div className="text-[12px] px-3 py-2 rounded-lg" style={{ background: "rgba(239,68,68,0.12)", color: "#FCA5A5" }}>{err}</div>}

                        <button onClick={submit} disabled={busy || !form.headline.trim()}
                            className="w-full py-2.5 rounded-full text-[13px] font-extrabold text-white inline-flex items-center justify-center gap-2 disabled:opacity-50"
                            style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})` }}
                            data-testid="ads-cinematic-submit">
                            {busy ? (<><Loader2 size={13} className="animate-spin" /> Rendering video (~60-90s)...</>) : "Genera video MP4"}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}

/* ========================== MEDIA THUMB (slot media output) ========================== */
function MediaThumb({ brandId, slotId, media, onRemoved }) {
    const [dataUri, setDataUri] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/ads/brands/${brandId}/assets/${media.asset_id}`);
                if (!cancelled) setDataUri(data?.data_uri || null);
            } catch (_e) { /* ignore */ }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [brandId, media.asset_id]);

    const remove = async (e) => {
        e.stopPropagation();
        if (!window.confirm("Rimuovere questa immagine?")) return;
        try {
            await api.delete(`/ads/brands/${brandId}/scheduled-posts/${slotId}/media/${media.order}`);
            if (onRemoved) onRemoved();
        } catch (_e) { /* ignore */ }
    };

    return (
        <div className="relative rounded-lg overflow-hidden aspect-square" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.10)" }}>
            {loading ? (
                <div className="absolute inset-0 grid place-items-center"><Loader2 size={14} className="animate-spin opacity-50" /></div>
            ) : dataUri ? (
                media.kind === "video" || (media.mime_type || "").startsWith("video/")
                    ? <video src={dataUri} className="w-full h-full object-cover" controls muted playsInline />
                    : <img src={dataUri} alt="" className="w-full h-full object-cover" />
            ) : (
                <div className="absolute inset-0 grid place-items-center opacity-40 text-[10px]">no preview</div>
            )}
            <button onClick={remove} className="absolute top-1 right-1 w-5 h-5 rounded-full grid place-items-center opacity-0 hover:opacity-100 transition" style={{ background: "rgba(0,0,0,0.7)" }} title="Rimuovi"><X size={10} /></button>
            <span className="absolute bottom-1 left-1 mono text-[9px] px-1 py-0.5 rounded" style={{ background: "rgba(0,0,0,0.6)", color: "#FFF" }}>{media.kind === "video" ? "▶ video" : `#${media.order + 1}`}</span>
        </div>
    );
}

/* ========================== GENERATE WIZARD ========================== */
function GenerateWizard({ brandId, onClose, onCreated }) {
    const [durationDays, setDurationDays] = useState(30);
    const [totalPosts, setTotalPosts] = useState(12);
    const [startDate, setStartDate] = useState(isoDate(new Date()));
    const [platforms, setPlatforms] = useState(["instagram", "linkedin"]);
    const [themeFocus, setThemeFocus] = useState("");
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState("");

    const togglePlatform = (id) => setPlatforms(prev =>
        prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setGenerating(true);
        try {
            const { data } = await api.post(`/ads/brands/${brandId}/editorial-plans/generate`, {
                total_posts: totalPosts,
                duration_days: durationDays,
                start_date: startDate,
                platforms,
                theme_focus: themeFocus || null,
            });
            onCreated(data.plan_id);
        } catch (err) {
            setError(formatApiError(err));
        }
        setGenerating(false);
    };

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(0,0,0,0.7)" }} onClick={onClose}>
            <form onSubmit={submit} className="rounded-2xl max-w-lg w-full p-6 space-y-4" style={{ background: "#1A1F36", border: "1px solid rgba(255,255,255,0.1)" }} onClick={(e) => e.stopPropagation()} data-testid="ads-wizard-modal">
                <div className="flex items-center justify-between">
                    <h2 className="text-[18px] font-extrabold inline-flex items-center gap-2"><Sparkles size={16} style={{ color: PRIMARY }} /> Genera piano editoriale</h2>
                    <button type="button" onClick={onClose} className="w-7 h-7 rounded-full grid place-items-center opacity-60" style={{ background: "rgba(255,255,255,0.06)" }}><X size={12} /></button>
                </div>

                <div>
                    <label className="text-[10.5px] mono uppercase tracking-wider opacity-60">Durata piano</label>
                    <div className="flex gap-2 mt-2">
                        {[7, 15, 30, 60].map(d => (
                            <button key={d} type="button" onClick={() => setDurationDays(d)}
                                className="flex-1 py-2 rounded-lg text-[13px] font-bold transition"
                                style={{
                                    background: durationDays === d ? `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})` : "rgba(255,255,255,0.04)",
                                    color: durationDays === d ? "#FFF" : "rgba(255,255,255,0.6)",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                }}
                                data-testid={`ads-wizard-duration-${d}`}>
                                {d}gg
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="text-[10.5px] mono uppercase tracking-wider opacity-60">Numero di post da generare: <b className="text-white">{totalPosts}</b></label>
                    <input type="range" min={3} max={Math.min(60, durationDays * 2)} value={totalPosts}
                        onChange={(e) => setTotalPosts(parseInt(e.target.value))}
                        className="w-full mt-2 accent-purple-500"
                        data-testid="ads-wizard-posts" />
                    <div className="text-[10.5px] opacity-50 mt-1">≈ {(totalPosts / (durationDays / 7)).toFixed(1)} post/settimana</div>
                </div>

                <div>
                    <label className="text-[10.5px] mono uppercase tracking-wider opacity-60">Data di partenza</label>
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                        className="w-full mt-1 bg-transparent text-[14px] mono px-3 py-2 rounded-lg outline-none"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} />
                </div>

                <div>
                    <label className="text-[10.5px] mono uppercase tracking-wider opacity-60">Piattaforme</label>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                        {PLATFORMS.map(p => (
                            <button key={p.id} type="button" onClick={() => togglePlatform(p.id)}
                                className="px-3 py-1.5 rounded-full text-[12px] font-bold transition"
                                style={{
                                    background: platforms.includes(p.id) ? "rgba(124,58,237,0.25)" : "rgba(255,255,255,0.04)",
                                    color: platforms.includes(p.id) ? "#C4B5FD" : "rgba(255,255,255,0.6)",
                                    border: `1px solid ${platforms.includes(p.id) ? "rgba(124,58,237,0.4)" : "rgba(255,255,255,0.08)"}`,
                                }}
                                data-testid={`ads-wizard-platform-${p.id}`}>
                                {p.id}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="text-[10.5px] mono uppercase tracking-wider opacity-60">Tema / focus (facoltativo)</label>
                    <textarea value={themeFocus} onChange={(e) => setThemeFocus(e.target.value)}
                        rows={2} placeholder="Es: lancio nuova collezione estiva, focus sostenibilità..."
                        className="w-full mt-1 bg-transparent text-[13px] px-3 py-2 rounded-lg outline-none resize-none"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                        data-testid="ads-wizard-theme" />
                </div>

                {error && <div className="text-[12px] px-3 py-2 rounded-lg" style={{ background: "rgba(239,68,68,0.12)", color: "#FCA5A5" }}>{error}</div>}

                <button type="submit" disabled={generating || platforms.length === 0}
                    className="w-full py-3 rounded-full font-extrabold text-[14px] text-white disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, boxShadow: `0 10px 28px ${PRIMARY}45` }}
                    data-testid="ads-wizard-submit">
                    {generating ? "Generazione in corso (15-30s)..." : (<><Wand2 size={14} /> Genera con AI</>)}
                </button>
            </form>
        </div>
    );
}

/* ========================== PLATFORM BADGES ========================== */
function PlatformBadges({ platforms, compact }) {
    return (
        <div className="inline-flex gap-1">
            {platforms.map(p => {
                const meta = PLATFORMS.find(x => x.id === p) || { label: p, color: "#888" };
                return (
                    <span key={p} className={`mono font-extrabold ${compact ? "text-[8px]" : "text-[9px]"} rounded ${compact ? "px-1 py-0" : "px-1.5 py-0.5"}`}
                        style={{
                            background: `${meta.color}26`, color: meta.color,
                            border: `1px solid ${meta.color}50`,
                        }}>{meta.label}</span>
                );
            })}
        </div>
    );
}

/* ========================== UTILS ========================== */
function buildMonthGrid(refDate) {
    const y = refDate.getFullYear();
    const m = refDate.getMonth();
    const first = new Date(y, m, 1);
    // monday-first weekStart
    const dow = (first.getDay() + 6) % 7;
    const start = new Date(y, m, 1 - dow);
    const cells = [];
    for (let i = 0; i < 42; i++) {
        cells.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
    }
    return cells;
}

function addMonths(d, n) {
    return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function monthLabel(d) {
    return d.toLocaleDateString("it-IT", { month: "long", year: "numeric" })
        .replace(/^./, c => c.toUpperCase());
}

function formatLongDate(iso) {
    try {
        const d = new Date(iso + "T00:00:00");
        return d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })
            .replace(/^./, c => c.toUpperCase());
    } catch { return iso; }
}
